import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useGame } from '../../contexts/GameContext'
import { apiUrl } from '../../config/api'
import UserDisplay from '../UserDisplay/UserDisplay'
import UserBadge from '../UserBadge/UserBadge'
import './PublicProfile.css'

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1'

export default function PublicProfile({ username, onBack, onViewReplay }) {
  const { user, session, isGuest } = useAuth()
  const { getMatchHistory, getRatingHistory } = useGame()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [matchHistory, setMatchHistory] = useState([])
  const [ratingHistory, setRatingHistory] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [loadingMoreGames, setLoadingMoreGames] = useState(false)
  const [tooltip, setTooltip] = useState({ show: false, text: '', x: 0, y: 0 })
  const [hasMoreGames, setHasMoreGames] = useState(true)
  const [friendRequestSent, setFriendRequestSent] = useState(false)
  const [friendshipStatus, setFriendshipStatus] = useState('unknown') // 'unknown', 'accepted', 'pending', 'none'

  useEffect(() => {
    if (username) {
      loadPublicProfile()
    }
  }, [username])

  const loadPublicProfile = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch public profile data
      const response = await fetch(apiUrl(`user/${username}`))
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('User not found')
        }
        throw new Error('Failed to load profile')
      }

      const profileData = await response.json()
      setProfile(profileData)

      // Load history data after profile loads
      loadHistoryData(profileData.id)

      // Check friendship status if not own profile
      const isUserOwnProfile = user && profileData && user.id === profileData.id
      if (!isUserOwnProfile) {
        checkFriendshipStatus(profileData.id)
      }
    } catch (err) {
      console.error('Failed to load public profile:', err)
      setError(err.message || 'Failed to load profile data')
    } finally {
      setLoading(false)
    }
  }

  const loadHistoryData = async (userId) => {
    try {
      setLoadingHistory(true)

      // Use the public API endpoints for match and rating history
      const [matchResponse, ratingResponse] = await Promise.all([
        fetch(apiUrl(`user/${username}/match-history?limit=10`)),
        fetch(apiUrl(`user/${username}/rating-history?limit=20`))
      ])

      let matches = []
      let ratings = []

      if (matchResponse.ok) {
        matches = await matchResponse.json()
      }
      if (ratingResponse.ok) {
        ratings = await ratingResponse.json()
      }

      setMatchHistory(Array.isArray(matches) ? matches : [])
      setRatingHistory(Array.isArray(ratings) ? ratings : [])

      // Check if there are more games to load
      setHasMoreGames(matches.length === 10)
    } catch (err) {
      console.error('Failed to load public history:', err)
      // Don't set main error, just log it as history is supplementary
      // Ensure we always have arrays, even if there's an error
      setMatchHistory([])
      setRatingHistory([])
      setHasMoreGames(false)
    } finally {
      setLoadingHistory(false)
    }
  }

  const loadMoreGames = async () => {
    if (!hasMoreGames || loadingMoreGames) return

    try {
      setLoadingMoreGames(true)

      const response = await fetch(apiUrl(`user/${username}/match-history?limit=10&offset=${matchHistory.length}`))

      if (response.ok) {
        const newMatches = await response.json()
        if (Array.isArray(newMatches) && newMatches.length > 0) {
          setMatchHistory(prev => [...prev, ...newMatches])
          setHasMoreGames(newMatches.length === 10)
        } else {
          setHasMoreGames(false)
        }
      } else {
        setHasMoreGames(false)
      }
    } catch (err) {
      console.error('Failed to load more games:', err)
      setHasMoreGames(false)
    } finally {
      setLoadingMoreGames(false)
    }
  }

  // Tooltip handlers for medals
  const handleMedalMouseEnter = (text) => (e) => {
    setTooltip({ show: true, text, x: e.clientX, y: e.clientY })
  }

  const handleMedalMouseMove = (e) => {
    if (tooltip.show) {
      setTooltip(prev => ({ ...prev, x: e.clientX, y: e.clientY }))
    }
  }

  const handleMedalMouseLeave = () => {
    setTooltip({ show: false, text: '', x: 0, y: 0 })
  }

  const checkFriendshipStatus = async (userId) => {
    if (!user || !session?.access_token) {
      setFriendshipStatus('none')
      return
    }

    try {
      const token = session.access_token
      const response = await fetch(`${API_BASE}/friends/${userId}/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const statusData = await response.json()
        console.log('Friendship status response:', statusData)
        setFriendshipStatus(statusData.status || 'none')
      } else {
        console.log('Friendship status request failed:', response.status)
        setFriendshipStatus('none')
      }
    } catch (err) {
      console.error('Failed to check friendship status:', err)
      setFriendshipStatus('none')
    }
  }

  const sendFriendRequest = async () => {
    if (!user || !session?.access_token || !profile?.id) return

    try {
      const token = session.access_token
      const response = await fetch(`${API_BASE}/friends/request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          receiver_id: profile.id,
          message: '',
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to send friend request')
      }

      setFriendRequestSent(true)
      setFriendshipStatus('request_sent')
      if (window.showToast) {
        window.showToast(`Friend request sent to ${profile.username}!`, 'success')
      }
    } catch (err) {
      console.error('Error sending friend request:', err)
      if (window.showToast) {
        window.showToast(err.message, 'error')
      }
    }
  }

  if (loading) {
    return (
      <div className="public-profile-container">
        <div className="public-profile-header">
          <div className="public-profile-logo">
            <img
              src="/images/logo/finallogosquare.png"
              alt="RiftChess"
              className="logo-icon"
            />
          </div>
          <h1>Player Profile</h1>
        </div>
        <div className="public-profile-loading">
          <div className="loading-spinner">Loading profile...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="public-profile-container">
        <div className="public-profile-header">
          <div className="public-profile-logo">
            <img
              src="/images/logo/finallogosquare.png"
              alt="RiftChess"
              className="logo-icon"
            />
          </div>
          <h1>Player Profile</h1>
        </div>
        <div className="public-profile-error">
          <p>{error}</p>
          <button className="retry-button" onClick={loadPublicProfile}>
            Try Again
          </button>
        </div>
      </div>
    )
  }

  const stats = profile ? {
    rating: profile.rating || 1200,
    gamesPlayed: profile.games_played || 0,
    gamesWon: profile.games_won || 0,
    winRate: profile.games_played > 0 ? Math.round((profile.games_won / profile.games_played) * 100) : 0,
    totalEarnings: profile.total_earnings || 0,
    rank: profile.rank || null
  } : null

  const isOwnProfile = user && profile && user.id === profile.id

  // Check if user is a beta account (created before a certain date)
  const isBetaAccount = profile && new Date(profile.created_at) < new Date('2026-02-01')

  // Check if user has premium account
  const isPremiumAccount = profile && profile.account_type === 'premium'

  // Check for badges
  const isStreamer = profile && profile.is_streamer
  const isGM = profile && profile.is_gm
  const isIM = profile && profile.is_im
  const isWFM = profile && profile.is_wfm

  return (
    <div className={`public-profile-container ${isPremiumAccount ? 'premium' : ''}`}>
      <div className="public-profile-header">
        <div className="profile-header-content">
          <div className={`profile-avatar ${isPremiumAccount ? 'premium-avatar' : ''}`}>
            <img
              src="/images/logo/finallogosquare.png"
              alt={`${profile?.username || 'Player'} Avatar`}
              className="profile-image"
            />
          </div>
          <div className="profile-info">
            <div className="profile-name-section">
              <div className="profile-header-row">
                {!isOwnProfile && !isGuest && user && friendshipStatus !== 'unknown' && (
                  <button
                    className={`add-friend-btn-round ${friendshipStatus === 'accepted' ? 'friends-btn' : ''}`}
                    onClick={friendshipStatus === 'accepted' ? undefined : sendFriendRequest}
                    disabled={friendRequestSent || friendshipStatus === 'pending' || friendshipStatus === 'accepted'}
                    title={`${friendshipStatus === 'accepted' ? 'Friends' :
                           friendshipStatus === 'pending' ? 'Request Pending' : 'Add Friend'} (Status: ${friendshipStatus})`}
                  >
                    <span style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                      {friendshipStatus === 'accepted' ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <span style={{ fontSize: '0.75em', color: '#22c55e', fontWeight: 'bold' }}>✓</span>
                          <span style={{ fontSize: '0.75em', color: '#22c55e', fontWeight: 'bold' }}>Friends</span>
                        </div>
                      ) : friendshipStatus === 'pending' ? '✓' :
                       <>
                         <span style={{ position: 'absolute', bottom: '2px', right: '2px', fontSize: '0.8em', fontWeight: 'bold', zIndex: 2, color: 'white', textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>+</span>
                         <span style={{ fontSize: '1em', opacity: 0.7 }}>👤</span>
                       </>}
                    </span>
                  </button>
                )}
              </div>
              <div className="name-section">
                <UserDisplay
                  user={profile}
                  usernameClass="profile-main-username"
                  showTitleBadges={true}
                  showStatusBadges={true}
                  context="profile"
                />
              </div>
            </div>

          </div>
        </div>
      </div>

      <div className="public-profile-content">
        {/* Top Row: Rating and Game Statistics */}
        <div className="top-row">
          {/* ELO Rating Section */}
          <div className="profile-card rating-card">
            <h2>Rating</h2>
            <div className="rating-display">
              <div className="rating-number">
                {stats?.rating || 1200}
              </div>
              <div className="rating-label">ELO Rating</div>
              {stats?.rank && (
                <div className="rank-info">
                  #{stats.rank} on leaderboard
                </div>
              )}
            </div>
          </div>

          {/* Game Statistics Section */}
          <div className="profile-card stats-card">
            <h2>Game Statistics</h2>

            <div className="stats-grid-single-row">
              <div className="stat-item">
                <div className="stat-number">{stats?.gamesPlayed || 0}</div>
                <div className="stat-label">Games Played</div>
              </div>

              <div className="stat-item">
                <div className="stat-number">{stats?.gamesWon || 0}</div>
                <div className="stat-label">Games Won</div>
              </div>

              <div className="stat-item">
                <div className="stat-number">{stats?.winRate || 0}%</div>
                <div className="stat-label">Win Rate</div>
              </div>

              <div className="stat-item">
                <div className="stat-number">${stats?.totalEarnings || 0}</div>
                <div className="stat-label">Earnings</div>
              </div>
            </div>

            {stats?.gamesPlayed === 0 && (
              <div className="no-games-message">
                <p>No games played yet.</p>
              </div>
            )}
          </div>
        </div>

        {/* Medals & Achievements Section - Full Width */}
        <div className="profile-card medals-card">
          <h2>Medals & Achievements</h2>
          <div className="medals-container">

            {/* Beta Player Badge */}
            {isBetaAccount && (
              <div
                className="medal beta-medal badge-with-image"
                onMouseEnter={handleMedalMouseEnter('Beta Player - Early Adopter')}
                onMouseMove={handleMedalMouseMove}
                onMouseLeave={handleMedalMouseLeave}
              >
                <img
                  src="/images/badges/betabadge.png"
                  alt="Beta Player"
                  className="badge-image"
                />
              </div>
            )}

            {/* Additional medals can be added here based on user achievements */}
            {profile && profile.games_played > 50 && (
              <div
                className="medal games-medal badge-with-image"
                onMouseEnter={handleMedalMouseEnter('Active Player - 50+ games played')}
                onMouseMove={handleMedalMouseMove}
                onMouseLeave={handleMedalMouseLeave}
              >
                <img
                  src="/images/badges/activebadge.png"
                  alt="Active Player"
                  className="badge-image"
                />
              </div>
            )}

            {profile && profile.games_won > 10 && (
              <div
                className="medal winner-medal badge-with-image"
                onMouseEnter={handleMedalMouseEnter('Winner - 10+ wins')}
                onMouseMove={handleMedalMouseMove}
                onMouseLeave={handleMedalMouseLeave}
              >
                <img
                  src="/images/badges/winnerbadge.png"
                  alt="Winner"
                  className="badge-image"
                />
              </div>
            )}

            {profile && stats?.winRate > 60 && stats?.gamesPlayed > 10 && (
              <div
                className="medal skilled-medal badge-with-image"
                onMouseEnter={handleMedalMouseEnter('Skilled Player - 60%+ win rate')}
                onMouseMove={handleMedalMouseMove}
                onMouseLeave={handleMedalMouseLeave}
              >
                <img
                  src="/images/badges/winratebadge.png"
                  alt="Skilled Player"
                  className="badge-image"
                />
              </div>
            )}
          </div>
        </div>

        {/* User Info Section - Full Width Below */}
        <div className="profile-card user-info-card">
          <h2>Player Information</h2>

          <div className="user-info-section">
            <label>Username</label>
            <div className="user-info-value">
              {profile?.username || 'Unknown'}
            </div>
          </div>

          <div className="user-info-section">
            <label>Member Since</label>
            <div className="user-info-value">
              {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : 'Unknown'}
            </div>
          </div>

          {stats?.rank && (
            <div className="user-info-section">
              <label>Leaderboard Rank</label>
              <div className="user-info-value rank-value">
                #{stats.rank}
              </div>
            </div>
          )}
        </div>

        {/* Rating History Chart */}
        <div className="profile-card rating-history-card">
          <h2>Rating History</h2>
          {loadingHistory ? (
            <div className="history-loading">Loading rating history...</div>
          ) : ratingHistory && ratingHistory.length > 0 ? (
            <div className="rating-chart">
              <div className="rating-chart-container">
                {ratingHistory.slice(-20).map((entry, index) => {
                  const height = Math.max(10, (entry.rating_after - 1000) / 10)
                  const isPositive = entry.rating_change > 0
                  return (
                    <div key={entry.game_id || index} className="rating-bar">
                      <div
                        className={`rating-point ${isPositive ? 'positive' : 'negative'}`}
                        style={{ height: `${height}px` }}
                        title={`${entry.rating_after} (${isPositive ? '+' : ''}${entry.rating_change}) - ${entry.result}`}
                      />
                    </div>
                  )
                })}
              </div>
              <div className="rating-chart-info">
                <p>Last 20 rated games showing rating changes</p>
                <div className="rating-legend">
                  <span className="legend-item positive">Win/Draw with gain</span>
                  <span className="legend-item negative">Loss or rating decrease</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="no-rating-history">
              <p>No rating history available.</p>
            </div>
          )}
        </div>

        {/* Match History */}
        <div className="profile-card match-history-card">
          <h2>Recent Games</h2>
          {loadingHistory ? (
            <div className="history-loading">Loading match history...</div>
          ) : matchHistory && matchHistory.length > 0 ? (
            <div className="match-history-list">
              {matchHistory.map((match) => {
                const isWhite = match.white_player_id === profile?.id
                const opponent = isWhite ? match.black_player : match.white_player
                const playerRatingBefore = isWhite ? match.white_rating_before : match.black_rating_before
                const playerRatingAfter = isWhite ? match.white_rating_after : match.black_rating_after
                const ratingChange = playerRatingAfter - playerRatingBefore

                let result = 'Draw'
                let resultClass = 'draw'
                if (match.result === 'white_win') {
                  result = isWhite ? 'Won' : 'Lost'
                  resultClass = isWhite ? 'win' : 'loss'
                } else if (match.result === 'black_win') {
                  result = isWhite ? 'Lost' : 'Won'
                  resultClass = isWhite ? 'loss' : 'win'
                }

                return (
                  <div key={match.id} className="match-history-item">
                    <div className="match-info">
                      <div className="match-result">
                        <span className={`result-badge ${resultClass}`}>{result}</span>
                      </div>
                      <div className="match-details">
                        <div className="opponent">
                          vs {opponent?.username || 'Anonymous'}
                        </div>
                        <div className="match-meta">
                          {isWhite ? 'White' : 'Black'} • {new Date(match.ended_at || match.updated_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div className="match-rating">
                      {playerRatingAfter === 0 || playerRatingBefore === 0 ? (
                        <div className="rating-unranked">
                          {match.tournament_id ? 'TOURNAMENT' : 'UNRANKED'}
                        </div>
                      ) : (
                        <>
                          <div
                            className={`rating-change ${ratingChange < 0 ? 'negative' : 'positive'}`}
                          >
                            {ratingChange > 0 ? '+' : ''}{ratingChange}
                          </div>
                          <div className="rating-value">
                            {playerRatingAfter}
                          </div>
                        </>
                      )}
                    </div>
                    <div className="match-actions">
                      <button
                        className="replay-button"
                        onClick={() => onViewReplay && onViewReplay(match.id)}
                        title="View replay"
                      >
                        ▶
                      </button>
                    </div>
                  </div>
                )
              })}
              {hasMoreGames && (
                <div className="load-more-section">
                  <button
                    className="load-more-button"
                    onClick={loadMoreGames}
                    disabled={loadingMoreGames}
                  >
                    {loadingMoreGames ? 'Loading...' : 'Load More Games'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="no-match-history">
              <p>No game history available.</p>
            </div>
          )}
        </div>
      </div>

      {/* Tooltip */}
      {tooltip.show && (
        <div
          className="badge-tooltip"
          style={{
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  )
}